/**
 * Insights resource (CT-5716): patterns in a person's emails, calls and meetings
 * and how often they hold, listed on the Intelligence tab and opened one at a
 * time in the profile slide-out.
 *
 * No contract has been shared yet, so every path below is ASSUMED and marked
 * `TODO(CT-5716 / backend)` — reconcile at cutover. Real signatures and
 * snake_case bodies; only the transport is stubbed (`mockTransport.js`).
 *
 * Narrowing (chip, status, search) and the named sort happen on the server.
 */

import { validateApiResponse } from '../shared/utils/apiResponse.js';
import { mapHistoryEntries } from '../shared/utils/historyEntries.js';
import { request } from './mockTransport.js';

export {
  __resetMockIntelligenceForTests,
  __setMockIntelligenceScenario,
} from './__mocks__/intelligence.mock.js';

const numberOrNull = (value) => (typeof value === 'number' ? value : null);

/**
 * Map one insight card. `progress` is `null` when the insight has no
 * percentage (a "Consistent since August" kind of headline): the card then
 * draws no bar rather than an empty one.
 */
export const mapApiInsightToUi = (raw = {}) => ({
  id: raw.id ?? raw.insight_id ?? raw.insightId ?? '',
  category: raw.category ?? null,
  title: raw.title ?? '',
  trend: raw.trend ?? null,
  value: raw.value ?? null,
  headline: raw.headline ?? null,
  progress: numberOrNull(raw.progress),
  confidence: numberOrNull(raw.confidence),
  sourceLine: raw.source_line ?? raw.sourceLine ?? null,
  evidence: raw.evidence ?? [],
  hasAttempts: Boolean(raw.has_attempts ?? raw.hasAttempts ?? false),
  note: raw.note ?? raw.paused_note ?? raw.pausedNote ?? null,
  paused: Boolean(raw.paused ?? false),
});

const mapSource = (raw = {}) => ({
  id: raw.id ?? raw.source_id ?? '',
  kind: raw.kind ?? null,
  label: raw.label ?? raw.kind ?? '',
  person: raw.person ?? null,
  occurredAt: raw.occurred_at ?? raw.occurredAt ?? null,
  // A date with no time ("2026-09-02") is a day, not midnight UTC: the time is shown only when sent.
  hasTime: String(raw.occurred_at ?? raw.occurredAt ?? '').includes('T'),
  outcome: raw.outcome ?? null,
  historyEventId: raw.history_event_id ?? raw.historyEventId ?? null,
});

/** Map one insight's detail. The recalculation text is the payload's, never a constant. */
export const mapApiInsightDetailToUi = (raw = {}) => ({
  ...mapApiInsightToUi(raw),
  summary: raw.summary ?? null,
  window: raw.window ?? null,
  sample: raw.sample ?? null,
  sources: (raw.sources ?? []).map(mapSource),
  sourcesTotal: numberOrNull(raw.sources_total ?? raw.sourcesTotal),
  recalculationText: raw.recalculation_text ?? raw.recalculationText ?? null,
  usedIn: raw.used_in ?? raw.usedIn ?? [],
  visibility: raw.visibility ?? null,
  history: mapHistoryEntries(raw.history),
  reportOpen: Boolean(raw.report_open ?? raw.reportOpen ?? false),
});

/**
 * TODO(CT-5716 / backend): `POST /v0/profile/insights/list` with `{ entity_type,
 * entity_id, quick_filter, order_by, order_direction, filters: { status },
 * search: { query, fields } }`.
 */
export const listInsights = async ({
  entityType,
  entityId,
  quickFilter = 'all',
  orderBy = 'recently_changed',
  orderDirection = 'asc',
  filters,
  search,
} = {}) => {
  const response = await request('/v0/profile/insights/list', {
    entity_type: entityType,
    entity_id: entityId,
    quick_filter: quickFilter,
    order_by: orderBy,
    order_direction: orderDirection,
    ...(filters ? { filters } : {}),
    ...(search ? { search } : {}),
  });
  return validateApiResponse(response, 'Failed to load the insights');
};

/** TODO(CT-5716 / backend): `POST /v0/profile/insights/get` with `{ entity_type, entity_id, insight_id }`. */
export const getInsight = async ({ entityType, entityId, insightId } = {}) => {
  const response = await request('/v0/profile/insights/get', {
    entity_type: entityType,
    entity_id: entityId,
    insight_id: insightId,
  });
  return validateApiResponse(response, 'Failed to load the insight');
};

/** TODO(CT-5716 / backend): `POST /v0/profile/insights/report` with `{ entity_type, entity_id, insight_id }`. */
export const reportInsightIssue = async ({ entityType, entityId, insightId } = {}) => {
  const response = await request('/v0/profile/insights/report', {
    entity_type: entityType,
    entity_id: entityId,
    insight_id: insightId,
  });
  return validateApiResponse(response, 'Failed to report the issue');
};
